import { PhotoExportClassType, PhotoExportClassTypeItem } from 'shared'
import { createStrictContext } from '../../../../../utils/createContext'
import {
  PhotoExportRecordSelectContext,
  PhotoExportRecordSelectProviderProps,
} from './types'
import { useAnyTrue } from '../../../../../hooks/useAnyTrue'
import {
  usePhotoExportAsphaltLotOptionsQuery,
  usePhotoExportConcreteTestSetOptionsQuery,
  usePhotoExportControlPointOptionsQuery,
  usePhotoExportDensityLotOptionsQuery,
  usePhotoExportInspectionOptionsQuery,
  usePhotoExportPunchListOptionsQuery,
} from '../../../../../schema'
import { DefaultQueryHookOptions } from '../../../../../utils/defaults'
import { useGlobalState } from '../../../../../context/GlobalStateContext'
import { useCallback } from 'react'
import { useOptions } from '../../../hooks/useOptions'

const { Provider, useContextFn } =
  createStrictContext<PhotoExportRecordSelectContext>(
    'PhotoExportRecordSelectContext'
  )

export const usePhotoExportRecordSelectContext = useContextFn

const PhotoExportRecordSelectProvider: React.FC<
  PhotoExportRecordSelectProviderProps
> = ({ children, classTypes }) => {
  const {
    state: { projectGlobalId },
  } = useGlobalState()
  const defaults = {
    ...DefaultQueryHookOptions,
    variables: { projectGlobalId: projectGlobalId! },
  }
  const In = useCallback<(classType: PhotoExportClassTypeItem) => boolean>(
    ({ id }) =>
      classTypes ? classTypes.some((classType) => id === classType.id) : true,
    [classTypes]
  )
  const asphaltLots = usePhotoExportAsphaltLotOptionsQuery({
    ...defaults,
    skip: !projectGlobalId || !In(PhotoExportClassType.AsphaltLot),
  })
  const asphaltLotOptions = useOptions({
    items: asphaltLots.data?.asphaltLots?.records,
    label: 'asphaltLotId',
    value: 'globalId',
    sort: 'asphaltLotId',
  })
  const concreteTestSets = usePhotoExportConcreteTestSetOptionsQuery({
    ...defaults,
    skip: !projectGlobalId || !In(PhotoExportClassType.ConcreteTestSet),
  })
  const concreteTestSetOptions = useOptions({
    items: concreteTestSets.data?.concreteTestSets?.records,
    label: 'testSetId',
    value: 'globalId',
    sort: 'globalId',
  })
  const controlPoints = usePhotoExportControlPointOptionsQuery({
    ...defaults,
    skip: !projectGlobalId || !In(PhotoExportClassType.ControlPoint),
  })
  const controlPointOptions = useOptions({
    items: controlPoints.data?.dailyInspectionControlPoints?.records,
    label: 'controlPointId',
    value: 'globalId',
    sort: 'controlPointId',
  })
  const densityLots = usePhotoExportDensityLotOptionsQuery({
    ...defaults,
    skip: !projectGlobalId || !In(PhotoExportClassType.DensityLot),
  })
  const densityLotOptions = useOptions({
    items: densityLots.data?.lots?.records,
    label: 'lotId',
    value: 'globalId',
    sort: 'lotId',
  })
  const inspections = usePhotoExportInspectionOptionsQuery({
    ...defaults,
    skip: !projectGlobalId || !In(PhotoExportClassType.Inspection),
  })
  const inspectionOptions = useOptions({
    items: inspections.data?.dailyInspectionInspections?.records,
    label: 'inspectionId',
    value: 'globalId',
    sort: 'inspectionId',
  })
  const punchLists = usePhotoExportPunchListOptionsQuery({
    ...defaults,
    skip: !projectGlobalId || !In(PhotoExportClassType.PunchList),
  })
  const punchListOptions = useOptions({
    items: punchLists.data?.dailyInspectionPunchLists?.records,
    label: 'punchListId',
    value: 'globalId',
    sort: 'punchListId',
  })
  const [loading] = useAnyTrue([
    asphaltLots.loading,
    concreteTestSets.loading,
    controlPoints.loading,
    densityLots.loading,
    inspections.loading,
    punchLists.loading,
  ])
  const options = useCallback<PhotoExportRecordSelectContext['options']>(
    (classType) => {
      if (classType === PhotoExportClassType.AsphaltLot)
        return asphaltLotOptions
      else if (classType === PhotoExportClassType.ConcreteTestSet)
        return concreteTestSetOptions
      else if (classType === PhotoExportClassType.ControlPoint)
        return controlPointOptions
      else if (classType === PhotoExportClassType.DensityLot)
        return densityLotOptions
      else if (classType === PhotoExportClassType.Inspection)
        return inspectionOptions
      else if (classType === PhotoExportClassType.PunchList)
        return punchListOptions
      return []
    },
    [
      asphaltLotOptions,
      concreteTestSetOptions,
      controlPointOptions,
      densityLotOptions,
      inspectionOptions,
      punchListOptions,
    ]
  )
  const allOptions = useOptions({
    items: PhotoExportClassType.values.filter(({ id }) => {
      if (classTypes) return classTypes.some((classType) => classType.id === id)
      return true
    }),
    type: 'custom',
    custom: (classType) => ({
      label: classType.classTypeName,
      value: classType.id,
      options: options(classType),
    }),
    sort: 'classTypeName',
  })

  return (
    <Provider value={{ allOptions, loading, options }}>{children}</Provider>
  )
}

export default PhotoExportRecordSelectProvider
